import { CaseType, type CaseUpdateDto, type ValidatorResult } from '@onrunning/backstage-models';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAnalytics } from '@/composables/analytics';
import { useNotification } from '@/composables/useNotification';
import { getBoxImageProxyUrl } from '@/composables/utils';
import type { CaseLine } from '@/models/case-line.model';
import type { Case } from '@/models/case.model';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import router from '@/router';
import { CasesService } from '@/services/cases.service';
import { useCaseStore } from '@/stores/case';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useUserStore } from '@/stores/user';
import type { ReturnCase, WarrantyCase } from '@/types/case.interface';
import type { MatrixOrderProduct } from '@/types/product.interface';
import { useFormat } from './format';

export interface UseCaseManagement {
  caseType: CaseType;
}

export function useCaseManagement(options: UseCaseManagement) {
  const casesService = new CasesService();

  const caseStore = useCaseStore();
  const userStore = useUserStore();
  const { formatError } = useFormat();
  const productMatrixStore = useProductMatrixStore();

  const { t } = useI18n();
  const { addCustomGaEvent } = useAnalytics();
  const { notifyError, notifySuccess } = useNotification();

  const saving = ref(false);
  const deleting = ref(false);
  const submitting = ref(false);

  const caseOrder = computed(() => {
    return (caseStore.updatedCaseOrder ?? caseStore.caseOrder) as WarrantyCase | ReturnCase;
  });

  const navigateBackToCases = () => {
    router.push(`/${options.caseType.toLowerCase()}-cases`);
  };

  const fetchCase = async (caseId: string, accountId: string): Promise<WarrantyCase | ReturnCase> => {
    const caseValidation: ValidatorResult<any> = await casesService.get(caseId, accountId);

    if (!caseValidation.isValid() || !caseValidation.model) {
      throw new Error(formatError(caseValidation.errors) || t('viewCases.no_cases_message'));
    }

    caseStore.setCase(caseValidation.model);

    return caseValidation.model as WarrantyCase | ReturnCase;
  };

  const submitCase = async () => {
    submitting.value = true;

    try {
      const payload = { ...caseOrder.value } as CaseUpdateDto;

      await casesService.update(caseOrder.value.id, payload, userStore.selectedAccount.id);

      const submitResult = await casesService.submit(caseOrder.value.id, userStore.selectedAccount.id);

      router.push(`/${options.caseType.toLocaleLowerCase()}-cases`);

      if (submitResult.isValid()) {
        addCustomGaEvent({
          event: GaCustomEvents.SubmitReturn,
          return_type: options.caseType,
        });
        notifySuccess(t(`viewCase.submit_${options.caseType.toLowerCase()}_success`));
        navigateBackToCases();
      } else {
        notifyError(formatError(submitResult.errors) || t('backend.cases.submit_case_error'));
      }
    } catch (error) {
      notifyError(t('backend.cases.submit_case_error'));
    } finally {
      submitting.value = false;
    }
  };

  const createCase = async (): Promise<WarrantyCase | ReturnCase> => {
    try {
      saving.value = true;

      const caseOrder = {
        accountId: userStore.selectedAccount.id,
        type: options.caseType,
      } as Case;

      if (options.caseType === CaseType.Warranty) {
        caseOrder.lines = warrantyCaseLines();
      }
      if (options.caseType === CaseType.Return) {
        caseOrder.lines = returnCaseLines();
      }

      const result = (await casesService.create(caseOrder, userStore.selectedAccount.id)) as ValidatorResult<any>;
      if (!result.isValid()) {
        throw new Error(formatError(result.errors) || t('backend.cases.submission_error'));
      }

      return result.model;
    } finally {
      saving.value = false;
    }
  };

  const updateCase = async (): Promise<WarrantyCase | ReturnCase> => {
    try {
      saving.value = true;

      const payload = { ...caseOrder.value } as CaseUpdateDto;

      if (productMatrixStore.matrixUpdated) {
        if (options.caseType === CaseType.Warranty) {
          payload.lines = warrantyUpdatedCaseLines(caseOrder.value.lines);
        }
        if (options.caseType === CaseType.Return) {
          payload.lines = returnCaseLines();
        }
      }

      const result = await casesService.update(caseOrder.value.id, payload, userStore.selectedAccount.id);
      if (!result.isValid()) {
        throw new Error(formatError(result.errors) || t('backend.cases.update_case_error'));
      }
      const updatedCaseOrder = await fetchCase(caseOrder.value.id, userStore.selectedAccount.id);

      return updatedCaseOrder;
    } finally {
      saving.value = false;
    }
  };

  const deleteCase = async () => {
    deleting.value = true;
    try {
      await casesService.remove(caseOrder.value.id, userStore.selectedAccount.id);

      router.push(`/${options.caseType.toLocaleLowerCase()}-cases`);
    } catch (error) {
      notifyError(t('backend.cases.delete_case_error'));
    } finally {
      deleting.value = false;
    }
  };

  const upsertCase = async () => {
    if (!caseStore.caseUpdated && !productMatrixStore.matrixUpdated) {
      return;
    }

    let createdCase: WarrantyCase | ReturnCase;

    if (caseOrder.value?.id) {
      createdCase = await updateCase();
    } else {
      createdCase = await createCase();
    }

    caseStore.setUpdatedCase(createdCase);
    productMatrixStore.updateMatrixOrder({ orderId: createdCase.id, orderNumber: createdCase.caseNumber });
    caseStore.setUpdateStatus(false);
    productMatrixStore.setUpdateStatus(false);
  };

  const returnCaseLines = (): CaseLine[] => {
    const lines: CaseLine[] = [];

    Object.values(productMatrixStore.activeMatrixOrder?.matrixProducts).forEach((matrixProduct) => {
      const line = mapMatrixToCaseLine(matrixProduct);
      line.quantity = matrixProduct.quantity;
      lines.push(line);
    });

    return lines;
  };

  const warrantyCaseLines = (): CaseLine[] => {
    const lines: CaseLine[] = [];

    Object.values(productMatrixStore.activeMatrixOrder?.matrixProducts).forEach((matrixProduct) => {
      const maximumQuantityPerWarrantyLine = 1;

      for (let i = 0; i < matrixProduct.quantity; i++) {
        const line = mapMatrixToCaseLine(matrixProduct);
        line.quantity = maximumQuantityPerWarrantyLine;
        lines.push(line);
      }
    });

    return lines;
  };

  const warrantyUpdatedCaseLines = (caseOrderLines: CaseLine[] = []): CaseLine[] => {
    const lines: CaseLine[] = [];

    Object.values(productMatrixStore.activeMatrixOrder?.matrixProducts).forEach((matrixProduct) => {
      const maximumQuantityPerWarrantyLine = 1;

      for (let i = 0; i < matrixProduct.quantity; i++) {
        let line: CaseLine;
        const existingLineIndex = caseOrderLines.findIndex((cl) => cl.productId === matrixProduct.productId);

        if (existingLineIndex !== -1) {
          line = caseOrderLines[existingLineIndex];
          caseOrderLines.splice(existingLineIndex, 1);
        } else {
          line = mapMatrixToCaseLine(matrixProduct);
          line.quantity = maximumQuantityPerWarrantyLine;
        }

        lines.push(line);
      }
    });

    return lines;
  };

  const mapMatrixToCaseLine = (matrixProduct: MatrixOrderProduct): CaseLine => {
    const product = productMatrixStore.products[matrixProduct.productId];

    return {
      productId: product.productId,
      productMainGroup: product.mainGroup,
      productName: product.productName,
      productNumber: product.productGroupNumber,
      productFabricSize: `${product.color} ${product.sizeId}`,
      productGroupName: product.productGroupName,
      productImageUrl: getBoxImageProxyUrl(product.picUrl),
    } as CaseLine;
  };

  return {
    fetchCase,
    updateCase,
    submitCase,
    deleteCase,
    navigateBackToCases,
    createCase,
    upsertCase,
    saving,
    deleting,
    submitting,
  };
}
